import { Action } from 'models/action';
import {
    ActionConstruction,
    ActionConstructionPropertyValues,
    NumberOfUnitSettingsOption,
} from 'models/order-input/action-order';
import { ConstructionPropertyType } from 'models/order-input/shared';

export const extractPropertyTypes = (actions: Action[]): { type: ConstructionPropertyType; isRequired: boolean }[] => {
    const types = new Set<{ type: ConstructionPropertyType; isRequired: boolean }>();
    actions.forEach((action) => {
        if (action.settings) {
            Object.entries(action.settings).forEach(([key, value]) => {
                if (value?.include === true || value?.mandatory === true) {
                    // Skip number of units as it is not a construction property type
                    if (key !== 'numberOfUnits') {
                        const type = propertyTypeMap[key as keyof typeof propertyTypeMap];
                        if (type) {
                            types.add({ type, isRequired: value?.mandatory === true });
                        }
                    }
                }
            });
        }
    });
    return Array.from(types);
};

export const extractNumberOfUnitTypes = (actions: Action[]): NumberOfUnitSettingsOption[] => {
    const numberOfUnits: NumberOfUnitSettingsOption[] = [];
    actions.forEach((action) => {
        if (action.settings) {
            Object.entries(action.settings).forEach(([key, value]) => {
                if (value?.include === true || value?.mandatory === true) {
                    if (key === 'numberOfUnits') {
                        numberOfUnits.push({ ...value, actionId: action._id });
                    }
                }
            });
        }
    });
    return numberOfUnits;
};

export const emptyConstruction = () => {
    return {
        actions: [],
        warranty: false,
        settings: {} as ActionConstructionPropertyValues,
        animationId: '',
    } as ActionConstruction;
};

const propertyTypeMap = {
    claspMaterial: ConstructionPropertyType.CLASP_MATERIAL,
    claspTeeth: ConstructionPropertyType.CLASP_TEETH,
    claspType: ConstructionPropertyType.CLASP_TYPES,
    colorOrShade: ConstructionPropertyType.SHADE,
    contactType: ConstructionPropertyType.CONTACT_TYPE,
    dentureSteps: ConstructionPropertyType.DENTURE_STEPS,
    design: ConstructionPropertyType.METAL_DESIGN,
    expansionScrew: ConstructionPropertyType.EXPANSION_SCREW,
    facialLoop: ConstructionPropertyType.FACIAL_LOOP,
    implantSteps: ConstructionPropertyType.IMPLANT_STEPS,
    material: ConstructionPropertyType.MATERIAL,
    occlusion: ConstructionPropertyType.OCCLUSION_TYPE,
    orthColor: ConstructionPropertyType.ORTHODONTICS_COLORS,
    ponticDesign: ConstructionPropertyType.PONTIC_DESIGN,
    protrusion: ConstructionPropertyType.PROTRUSION,
    retainment: ConstructionPropertyType.RETAINMENT,
    springTeeth: ConstructionPropertyType.SPRING_TEETH,
    springType: ConstructionPropertyType.SPRING_TYPES,
    testingSteps: ConstructionPropertyType.TESTING_STEPS,
};

/**
 * Teeth option priority (highest to lowest):
 * 1. "Single tooth" - Per tooth selection
 * 2. "Range" - Range selection (start and stop)
 * 3. "Up/low jaw" - Upper and lower jaw selection
 * 4. null/undefined - No teeth selection
 */
const teethOptionPriority: Record<string, number> = {
    'Single tooth': 1,
    Range: 2,
    'Up/low jaw': 3,
};

/**
 * Aggregates teeth selection option from all actions in a construction.
 * Returns the highest priority teeth option found across all actions.
 * Priority: "Single tooth" > "Range" > "Up/low jaw" > null
 */
export const aggregateTeethOption = (actions: Action[]): string | null => {
    if (!actions || actions.length === 0) return null;

    let highestPriorityOption: string | null = null;
    let highestPriority = Infinity;

    actions.forEach((action) => {
        if (action.teethOption) {
            const priority = teethOptionPriority[action.teethOption] ?? Infinity;
            if (priority < highestPriority) {
                highestPriority = priority;
                highestPriorityOption = action.teethOption;
            }
        }
    });

    return highestPriorityOption;
};

/**
 * Aggregates materials from all actions in a construction.
 * Merges all materials from all actions' materialGroups and filters for unique values by name.
 */
export const aggregateMaterials = (
    actions: Action[],
): { availableMaterials: string[]; availableMaterialObjs: { _id?: string; material: string }[] } => {
    if (!actions || actions.length === 0) {
        return { availableMaterials: [], availableMaterialObjs: [] };
    }

    const materialMap = new Map<string, { _id?: string; material: string }>();

    actions.forEach((action) => {
        const group = action.materialGroup;
        if (group?.materials) {
            group.materials.forEach((mat) => {
                if (!materialMap.has(mat.material)) {
                    materialMap.set(mat.material, mat);
                }
            });
        }
    });

    const availableMaterialObjs = Array.from(materialMap.values());
    const availableMaterials = availableMaterialObjs.map((mat) => mat.material);

    return { availableMaterials, availableMaterialObjs };
};
